/*
Copyright (c) 2012-2022 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import update from "immutability-helper"
import { orderBy, find } from "lodash"
import {
  Item,
  Category,
  Section,
  Department,
  SubDepartment,
  SellingTime,
} from "types/models/menu"
import {
  getMartDepartmentList,
  getMartSubDepartmentList,
} from "utils/menu_helper"
import { MenuState } from "../menu.type"

export const handleSelectOverviewSubDepartment = (
  state: MenuState,
  subDepartment: SubDepartment,
) => {
  const items = orderBy(
    subDepartment?.items?.map(item => ({
      ...item,
      departmentID: subDepartment.departmentID, // inherit departmentID
      subDepartmentID: subDepartment.itemClassID, // inherit subDepartmentID
    })) ?? [],
    ["sortOrder"],
    ["desc"],
  )

  const nextState = update(state, {
    overview: {
      itemID: { $set: null },
      subDepartment: {
        $set: subDepartment,
      },
      items: {
        $set: items,
      },
    },
  })
  return nextState
}

export const handleSelectOverviewDepartment = (
  state: MenuState,
  department: Department,
) => {
  const subDepartments = orderBy(
    department?.subDepartments?.map(item => ({
      ...item,
      departmentID: department.itemClassID, // inherit departmentID
    })) ?? [],
    ["sortOrder"],
    ["desc"],
  )

  let nextState = update(state, {
    overview: {
      itemID: { $set: null },
      department: {
        $set: department,
      },
      subDepartments: {
        $set: subDepartments,
      },
    },
  })

  let { subDepartment } = state.overview
  subDepartment =
    find(
      subDepartments,
      ({ itemClassID }) => itemClassID === subDepartment?.itemClassID,
    ) ?? subDepartments[0]
  nextState = handleSelectOverviewSubDepartment(nextState, subDepartment)
  return nextState
}

export const handleSelectOverviewCategory = (
  state: MenuState,
  category: Category,
) => {
  const items = orderBy(
    category?.items?.map(item => ({
      ...item,
      categoryID: category.categoryID, // inherit categoryID
    })) ?? [],
    ["sortOrder"],
    ["desc"],
  )

  const nextState = update(state, {
    overview: {
      itemID: { $set: null },
      category: {
        $set: category,
      },
      items: {
        $set: items,
      },
    },
  })
  return nextState
}

export const handleSelectOverviewSection = (
  state: MenuState,
  section: Section,
) => {
  const categories = orderBy(
    section?.categories?.map(category => ({
      ...category,
      sectionID: section.sectionID, // inherit sectionID
    })) ?? [],
    ["sortOrder"],
    ["desc"],
  )

  const departments = orderBy(
    section?.departments?.map(department => ({
      ...department,
      sectionID: section.sectionID, // inherit sectionID
    })) ?? [],
    ["sortOrder"],
    ["desc"],
  )

  let nextState = update(state, {
    overview: {
      section: {
        $set: section,
      },
      categories: {
        $set: categories,
      },
      departments: {
        $set: departments,
      },
    },
  })

  if (state.isMartDepartmentStructure) {
    let { department } = state.overview
    department =
      find(
        departments,
        ({ itemClassID }) => itemClassID === department?.itemClassID,
      ) ?? departments[0]
    nextState = handleSelectOverviewDepartment(nextState, department)
  } else {
    let { category } = state.overview
    category =
      find(
        categories,
        ({ categoryID }) => categoryID === category?.categoryID,
      ) ?? categories[0]
    nextState = handleSelectOverviewCategory(nextState, category)
  }

  return nextState
}

export const handleSelectOverviewSellingTime = (
  state: MenuState,
  sellingTime: SellingTime | null,
) => {
  // filter based on item - get items with selected selling time and its category
  const categories = orderBy(
    (sellingTime
      ? state.menu.categories
          ?.filter(category =>
            category.items?.some(
              item => item.sellingTimeID === sellingTime.sellingTimeID,
            ),
          )
          .map(category => ({
            ...category,
            items: category.items?.filter(
              item => item.sellingTimeID === sellingTime.sellingTimeID,
            ),
          }))
      : state.menu.categories) || [],
    ["sortOrder"],
    ["desc"],
  )

  let departments: Department[] = []
  if (sellingTime) {
    orderBy(state.menu.departments || [], ["sortOrder"], ["desc"]).forEach(
      department => {
        // filter subdepartment by item
        const subDepartments = department.subDepartments
        const filteredSubDepartments = subDepartments
          ?.filter(subDepartment =>
            subDepartment.items?.some(
              item => item.sellingTimeID === sellingTime.sellingTimeID,
            ),
          )
          .map(subDepartment => ({
            ...subDepartment,
            items: subDepartment.items?.filter(
              item => item.sellingTimeID === sellingTime.sellingTimeID,
            ),
          }))
        // push department if has filtered subdepartments
        if (filteredSubDepartments.length) {
          departments.push({
            ...department,
            subDepartments: filteredSubDepartments,
          })
        }
      },
    )
  } else {
    departments = orderBy(state.menu.departments || [], ["sortOrder"], ["desc"])
  }

  let nextState = update(state, {
    overview: {
      sellingTime: {
        $set: sellingTime,
      },
      categories: {
        $set: categories,
      },
      departments: {
        $set: departments,
      },
    },
  })

  if (state.isMartDepartmentStructure) {
    let { department } = state.overview
    department =
      find(
        departments,
        ({ itemClassID }) => itemClassID === department?.itemClassID,
      ) ?? departments[0]
    nextState = handleSelectOverviewDepartment(nextState, department)
  } else {
    let { category } = state.overview
    category =
      find(
        categories,
        ({ categoryID }) => categoryID === category?.categoryID,
      ) ?? categories[0]
    nextState = handleSelectOverviewCategory(nextState, category)
  }

  return nextState
}

export const handleMultiSelectItems = (
  state: MenuState,
  items: Item[],
  value: boolean,
) => {
  const itemSelections = state.drawer.itemSelections ?? {}
  items.forEach(item => {
    if (item.itemID) {
      itemSelections[item.itemID] = value
    }
  })

  const nextState = update(state, {
    drawer: {
      itemSelections: {
        $set: { ...itemSelections },
      },
    },
  })

  return nextState
}

export const handleMultiSelectCategory = (
  state: MenuState,
  category: Category,
  value: boolean,
) => {
  const items = category.items ?? []
  const nextState = handleMultiSelectItems(state, items, value)
  return nextState
}

export const handleMultiSelectSubDepartment = (
  state: MenuState,
  subDepartment: SubDepartment,
  value: boolean,
) => {
  const items = subDepartment.items ?? []
  const nextState = handleMultiSelectItems(state, items, value)
  return nextState
}

export const handleMultiSelectDepartment = (
  state: MenuState,
  department: Department,
  value: boolean,
) => {
  const subDepartments = department.subDepartments ?? []
  let items: Item[] = []
  subDepartments.forEach(subDepartment => {
    items = [...items, ...subDepartment.items]
  })
  const nextState = handleMultiSelectItems(state, items, value)
  return nextState
}

export const handleSelectSectionByID = (
  state: MenuState,
  sectionID: string,
) => {
  const section = find(state.menu.sections, s => s.sectionID === sectionID)
  const nextState = section
    ? handleSelectOverviewSection(state, section)
    : state
  return nextState
}

export const handleSelectCategoryByID = (
  state: MenuState,
  categoryID: string,
) => {
  let nextState = state

  const category = find(state.categories, c => c.categoryID === categoryID)

  const section = find(
    state.menu.sections,
    s => s.sectionID === category?.sectionID,
  )

  if (section) nextState = handleSelectOverviewSection(state, section)

  if (category) nextState = handleSelectOverviewCategory(nextState, category)

  return nextState
}

export const handleSelectDepartmentByID = (
  state: MenuState,
  departmentID: string,
) => {
  let nextState = state

  const department = find(
    state.departments,
    d => d.itemClassID === departmentID,
  )

  const section = find(
    state.menu.sections,
    s => s.sectionID === department?.sectionID,
  )

  if (section) nextState = handleSelectOverviewSection(state, section)

  if (department) nextState = handleSelectOverviewDepartment(state, department)

  return nextState
}

export const handleSelectSubDepartmentByID = (
  state: MenuState,
  subDepartmentID: string,
) => {
  let nextState = state

  const subDepartment = find(
    state.subDepartments,
    d => d.itemClassID === subDepartmentID,
  )

  const department = find(
    state.departments,
    d => d.itemClassID === subDepartment?.departmentID,
  )

  const section = find(
    state.menu.sections,
    s => s.sectionID === department?.sectionID,
  )

  if (section) nextState = handleSelectOverviewSection(state, section)

  if (department) nextState = handleSelectOverviewDepartment(state, department)

  if (subDepartment)
    nextState = handleSelectOverviewSubDepartment(state, subDepartment)

  return nextState
}

export const handleSelectItemByID = (state: MenuState, itemID: string) => {
  const item = find(state.items, i => i.itemID === itemID)

  let nextState = state

  const section = find(
    state.menu.sections,
    s => s.sectionID === item?.sectionID,
  )

  if (section) nextState = handleSelectOverviewSection(state, section)

  if (state.isMartDepartmentStructure) {
    const department = find(
      getMartDepartmentList(state.menu),
      d => d.itemClassID === item?.departmentID,
    )

    if (department)
      nextState = handleSelectOverviewDepartment(nextState, department)

    const subDepartment = find(
      getMartSubDepartmentList(state.menu),
      d => d.itemClassID === item?.subDepartmentID,
    )

    if (subDepartment)
      nextState = handleSelectOverviewSubDepartment(nextState, subDepartment)
  } else {
    const category = find(
      state.categories,
      c => c.categoryID === item?.categoryID,
    )
    if (category) nextState = handleSelectOverviewCategory(nextState, category)
  }

  return nextState
}
